import { lumi } from '@/lib/lumi'
import { publicProfilePayload, sanitizeProfile, sanitizeProfiles } from '@/lib/publicData'
import { getColorScheme, getStoreLayout } from '@/lib/storeTheme'
import { slugifyStoreName } from '@/lib/storeSlug'
import { useCallback, useEffect, useState } from 'react'

export interface Profile {
  _id: string
  userId: string
  storeName: string
  storeSlug?: string
  bio?: string
  avatarUrl?: string
  bannerUrl?: string
  backgroundUrl?: string
  storeLayout?: string
  colorScheme?: string
  favoriteGames?: string[]
  tcgplayerStoreUrl?: string
  ebayStoreUrl?: string
  tiktokUrl?: string
  xUrl?: string
  facebookUrl?: string
  instagramUrl?: string
  websiteUrl?: string
  creator: string
  createdAt: string
  updatedAt: string
}

async function isSlugTaken(slug: string, excludeUserId?: string) {
  const { list } = await lumi.entities.profiles.list({ filter: { storeSlug: slug }, limit: 5 })
  return ((list as Profile[]) || []).some(profile => profile.userId !== excludeUserId)
}

async function uniqueStoreSlug(storeName: string, excludeUserId?: string) {
  const base = slugifyStoreName(storeName)
  let candidate = base
  let suffix = 2

  while (await isSlugTaken(candidate, excludeUserId)) {
    candidate = `${base}${suffix}`
    suffix += 1
  }

  return candidate
}

export function useProfile(userId?: string) {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchProfile = useCallback(async () => {
    if (!userId) {
      setProfile(null)
      return
    }
    setLoading(true)
    try {
      const { list } = await lumi.entities.profiles.list({ filter: { userId }, limit: 1 })
      setProfile(sanitizeProfile((list?.[0] as Profile) || null))
    } catch (error) {
      console.error('Failed to fetch profile:', error)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  const saveProfile = async (data: Partial<Profile>) => {
    const ownerId = lumi.auth.user?.userId
    if (!lumi.auth.isAuthenticated || !ownerId) {
      throw new Error('Sign in to manage your store profile.')
    }
    if (userId && userId !== ownerId) {
      throw new Error('You can only edit your own store profile.')
    }
    if (profile?.userId && profile.userId !== ownerId) {
      throw new Error('You can only edit your own store profile.')
    }

    const now = new Date().toISOString()
    const nextName = (data.storeName || profile?.storeName || 'MyStore').trim()
    const nameChanged = nextName !== (profile?.storeName || '').trim()
    const storeSlug = !nameChanged && profile?.storeSlug
      ? profile.storeSlug
      : await uniqueStoreSlug(nextName, ownerId)

    try {
      if (profile?._id) {
        const updated = await lumi.entities.profiles.update(profile._id, publicProfilePayload({
          ...data,
          storeName: nextName,
          storeSlug,
          storeLayout: getStoreLayout(data.storeLayout ?? profile.storeLayout),
          colorScheme: getColorScheme(data.colorScheme ?? profile.colorScheme),
          updatedAt: now,
        }))
        await fetchProfile()
        return updated
      }
      const created = await lumi.entities.profiles.create(publicProfilePayload({
        userId: ownerId,
        storeName: nextName,
        storeSlug,
        bio: data.bio || '',
        avatarUrl: data.avatarUrl || '',
        bannerUrl: data.bannerUrl || '',
        backgroundUrl: data.backgroundUrl || '',
        storeLayout: getStoreLayout(data.storeLayout),
        colorScheme: getColorScheme(data.colorScheme),
        favoriteGames: data.favoriteGames || [],
        tcgplayerStoreUrl: data.tcgplayerStoreUrl || '',
        ebayStoreUrl: data.ebayStoreUrl || '',
        tiktokUrl: data.tiktokUrl || '',
        xUrl: data.xUrl || '',
        facebookUrl: data.facebookUrl || '',
        instagramUrl: data.instagramUrl || '',
        websiteUrl: data.websiteUrl || '',
        createdAt: now,
        updatedAt: now,
      }))
      await fetchProfile()
      return created
    } catch (error) {
      console.error('Failed to save profile:', error)
      throw error
    }
  }

  return { profile, loading, saveProfile, refetch: fetchProfile }
}

export function useStoreBySlug(slug?: string) {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchProfile = useCallback(async () => {
    if (!slug) {
      setProfile(null)
      return
    }
    setLoading(true)
    try {
      const { list } = await lumi.entities.profiles.list({ filter: { storeSlug: slug }, limit: 1 })
      let match = sanitizeProfile((list?.[0] as Profile) || null)

      if (!match) {
        const { list: all } = await lumi.entities.profiles.list({ limit: 200 })
        match = sanitizeProfiles((all as Profile[]) || []).find(item => {
          const generated = (item.storeSlug || slugifyStoreName(item.storeName)).toLowerCase()
          return generated === slug.toLowerCase() || item.userId === slug
        }) || null
      }

      setProfile(match)
    } catch (error) {
      console.error('Failed to fetch store:', error)
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  return { profile, loading, refetch: fetchProfile }
}

export function useAllProfiles() {
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [loading, setLoading] = useState(false)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.profiles.list({ sort: { createdAt: -1 }, limit: 200 })
      setProfiles(sanitizeProfiles((list as Profile[]) || []))
    } catch (error) {
      console.error('Failed to fetch profiles:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const byUserId = new Map(profiles.map(p => [p.userId, p]))

  return { profiles, byUserId, loading, refetch: fetchAll }
}
